import { useMemo, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, useGallery } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { CTA_SOLID, CtaStyles, ctaOverLight } from './_cta';
import { amenityKeywordSets } from './_amenityIcons';
import { AmenityFaIcon } from './_amenityIconMap';

// ============================================================================
// Smart Photo Selection — pick a fitness / amenity-flavored gallery photo
// ============================================================================

const AMENITY_KEYWORDS = [
  'pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'amenities',
  'recreation', 'courtyard', 'grill', 'spa', 'theater', 'rooftop',
];

function photoText(photo: PhotoGallery): string {
  return [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

// Whole-word (with a simple plural) match so a keyword like "spa" can't hit
// "space" or "car" hit "carpet" — substring matching was a major source of
// unrelated photos being chosen.
function countWordHits(text: string, keywords: string[]): number {
  if (!text) return 0;
  let hits = 0;
  for (const kw of keywords) {
    const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`\\b${escaped}(?:s|es)?\\b`, 'i').test(text)) hits += 1;
  }
  return hits;
}

function useAmenityPhotoIndex(): number {
  const gallery = useGallery();
  return useMemo(() => {
    if (!gallery.length) return 3;
    let bestIndex = -1;
    let bestScore = 0;
    for (let i = 0; i < gallery.length; i++) {
      const score = countWordHits(photoText(gallery[i]), AMENITY_KEYWORDS);
      if (score > bestScore) {
        bestScore = score;
        bestIndex = i;
      }
    }
    if (bestIndex >= 0) return bestIndex;
    return Math.min(3, gallery.length - 1);
  }, [gallery]);
}

function makeImageSlotDef(index: number): SlotGroupDefinition {
  return {
    label: 'Amenities Image',
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Relevant gallery photo per amenity — best-effort, de-duplicated assignment
// ============================================================================
//
// Each callout tab should show the gallery photo that best matches its amenity
// title, and two different amenities should not land on the same photo when the
// gallery has enough variety. We score every (amenity, photo) pair and greedily
// assign the strongest matches first, never reusing a photo.
//
// Matching is deliberately conservative: curated category synonyms count far
// more than the amenity's own (often generic) title words, and a pair must clear
// MIN_MATCH_SCORE before it is eligible at all. That means a single incidental
// title-word hit — e.g. a mailroom caption mentioning "units" for a floor-plan
// amenity — is rejected, and the tab keeps its base editable image rather than
// showing an unrelated photo.

const CATEGORY_WEIGHT = 2;
const TITLE_WEIGHT = 1;
// A curated-category hit (2) clears this on its own; title words need two solid
// hits. A lone loose title-word match (1) never wins.
const MIN_MATCH_SCORE = 2;

function assignAmenityImages(
  titles: string[],
  gallery: PhotoGallery[],
): (string | null)[] {
  const result: (string | null)[] = titles.map(() => null);
  if (!gallery.length) return result;

  const galleryText = gallery.map(photoText);

  const pairs: { ti: number; pi: number; score: number }[] = [];
  titles.forEach((title, ti) => {
    const { category, title: titleWords } = amenityKeywordSets(title);
    if (!category.length && !titleWords.length) return;
    galleryText.forEach((text, pi) => {
      const score =
        countWordHits(text, category) * CATEGORY_WEIGHT +
        countWordHits(text, titleWords) * TITLE_WEIGHT;
      if (score >= MIN_MATCH_SCORE) pairs.push({ ti, pi, score });
    });
  });

  // Strongest matches win first; each photo is used by at most one amenity.
  pairs.sort((a, b) => b.score - a.score);
  const usedPhotos = new Set<number>();
  for (const { ti, pi } of pairs) {
    if (result[ti] || usedPhotos.has(pi)) continue;
    result[ti] = gallery[pi].image || null;
    usedPhotos.add(pi);
  }
  return result;
}

// ============================================================================
// Static slot group definitions (for contentSchema — must be inline literals)
// ============================================================================

const imageDef: SlotGroupDefinition = {
  label: 'Amenities Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Button',
  fields: {
    url: { label: 'Button URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Label', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// Six standout-amenity tabs. Rather than iterating the amenities collection,
// each tab is its own slot group so an editor can hand-pick exactly which six
// amenities headline the section — and a generator preselects the property's
// top amenities on demand. The card icon is derived from the chosen title via
// the ported amenity token map, so there is nothing extra to author per tab.
//
// Written as fully-inlined object literals (no shared factory, no referenced
// consts) so the preview system's static schema extractor can resolve them.
const amenity1Def: SlotGroupDefinition = {
  label: 'Standout Amenity 1',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[0].title', defaultValue: 'Resort-Style Pool' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 0, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Discover Amenities section.",
    context: 'Tab 1 of 6 in the Discover Amenities list — pick the single best headline amenity; each of the six tabs must be a different amenity.',
    sourceCollection: 'amenities',
  },
};

const amenity2Def: SlotGroupDefinition = {
  label: 'Standout Amenity 2',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[1].title', defaultValue: 'Fitness Center' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 1, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Discover Amenities section.",
    context: 'Tab 2 of 6 in the Discover Amenities list — pick a different amenity from the other five tabs.',
    sourceCollection: 'amenities',
  },
};

const amenity3Def: SlotGroupDefinition = {
  label: 'Standout Amenity 3',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[2].title', defaultValue: 'Dog Park' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 2, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Discover Amenities section.",
    context: 'Tab 3 of 6 in the Discover Amenities list — pick a different amenity from the other five tabs.',
    sourceCollection: 'amenities',
  },
};

const amenity4Def: SlotGroupDefinition = {
  label: 'Standout Amenity 4',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[3].title', defaultValue: 'Covered Parking' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 3, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Discover Amenities section.",
    context: 'Tab 4 of 6 in the Discover Amenities list — pick a different amenity from the other five tabs.',
    sourceCollection: 'amenities',
  },
};

const amenity5Def: SlotGroupDefinition = {
  label: 'Standout Amenity 5',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[4].title', defaultValue: 'Clubhouse' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 4, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Discover Amenities section.",
    context: 'Tab 5 of 6 in the Discover Amenities list — pick a different amenity from the other five tabs.',
    sourceCollection: 'amenities',
  },
};

const amenity6Def: SlotGroupDefinition = {
  label: 'Standout Amenity 6',
  fields: {
    title: { label: 'Amenity', type: 'text', defaultField: 'amenities[5].title', defaultValue: 'In-Home Laundry' },
  },
  sourceBinding: { collection: 'amenities', defaultIndex: 5, fieldMappings: { title: 'title' } },
  generator: {
    prompt: "Pick one of this property's top, most marketable amenities to feature in the Discover Amenities section.",
    context: 'Tab 6 of 6 in the Discover Amenities list — pick a different amenity from the other five tabs.',
    sourceCollection: 'amenities',
  },
};

// Representative titles for company-theme previews (no property to generate
// against). Each resolves to a distinct icon through the ported amenity map.
const TAB_PREVIEW_TITLES = [
  'Resort-Style Pool',
  'Fitness Center',
  'Dog Park',
  'Covered Parking',
  'Clubhouse',
  'In-Home Laundry',
];

// ============================================================================
// Presentational amenity tab (no hooks — lives inside each EditableSlotGroup)
// ============================================================================

interface AmenityTabProps {
  title: string;
  index: number;
  active: boolean;
  onSelect: (index: number) => void;
}

function AmenityTab({ title, index, active, onSelect }: AmenityTabProps) {
  return (
    <button
      type="button"
      className={`da-card${active ? ' da-card--active' : ''}`}
      aria-pressed={active}
      // Only swap the arched image when an amenity is actually chosen (click /
      // keyboard activation). Selecting on hover/focus caused the displayed
      // photo to flash between amenities as the pointer moved across the cards.
      onClick={() => onSelect(index)}
    >
      <AmenityFaIcon title={title} className="da-card-icon" />
      <span className="da-card-label">{title}</span>
    </button>
  );
}

// ============================================================================
// Component
// ============================================================================

export function TabbedCallouts() {
  const gallery = useGallery();
  const photoIndex = useAmenityPhotoIndex();
  const imgDef = useMemo(() => makeImageSlotDef(photoIndex), [photoIndex]);

  const img = useSlotGroup('tabbedcallouts.image', imgDef);
  const cta = useSlotGroup('tabbedcallouts.cta', ctaDef);

  const amenity1 = useSlotGroup('tabbedcallouts.amenity_1', amenity1Def);
  const amenity2 = useSlotGroup('tabbedcallouts.amenity_2', amenity2Def);
  const amenity3 = useSlotGroup('tabbedcallouts.amenity_3', amenity3Def);
  const amenity4 = useSlotGroup('tabbedcallouts.amenity_4', amenity4Def);
  const amenity5 = useSlotGroup('tabbedcallouts.amenity_5', amenity5Def);
  const amenity6 = useSlotGroup('tabbedcallouts.amenity_6', amenity6Def);

  // Resolve each tab's label — chosen/generated value first, preview title as
  // the non-property fallback so the widget never renders an empty card.
  const amenityTitles = [
    amenity1.title?.trim() || TAB_PREVIEW_TITLES[0],
    amenity2.title?.trim() || TAB_PREVIEW_TITLES[1],
    amenity3.title?.trim() || TAB_PREVIEW_TITLES[2],
    amenity4.title?.trim() || TAB_PREVIEW_TITLES[3],
    amenity5.title?.trim() || TAB_PREVIEW_TITLES[4],
    amenity6.title?.trim() || TAB_PREVIEW_TITLES[5],
  ];

  // Interactive tab state: the selected amenity highlights its card and swaps
  // the arched image to match.
  const [activeIndex, setActiveIndex] = useState(0);
  const safeIndex = activeIndex < amenityTitles.length ? activeIndex : 0;
  const activeTitle = amenityTitles[safeIndex];

  // Resolve the image shown for the active tab: give every amenity its own
  // best-matching (and distinct) gallery photo, then show the active tab's.
  // When nothing matches, stay null so the base editable image shows instead of
  // an unrelated photo.
  const amenityImages = useMemo(
    () => assignAmenityImages(amenityTitles, gallery),
    // amenityTitles is rebuilt each render; key the memo on its contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [amenityTitles.join(' '), gallery],
  );

  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up discover-amenities w-full"
    >
      <CtaStyles />
      <div className="da-grid">
        {/* ── Left: copy + amenities widget + CTA ── */}
        <div className="da-text">
          <RichTextContent
            path="tabbedcallouts.section_heading"
            label="Section Heading"
            defaultValue="<p>Discover Amenities</p>"
            as="h2"
            className="da-heading"
          />

          <RichTextContent
            path="tabbedcallouts.body"
            label="Body Text"
            defaultField="property_home_amenities_blurb"
            defaultValue='<p>At Verso, we offer a range of luxurious amenities designed to elevate your living experience. Enjoy resort-style features such as high-end fixtures, a resort-style pool, and beautifully landscaped outdoor spaces perfect for relaxation or socializing. Our pet-friendly apartment community also includes a dedicated dog park and pet-washing station ensuring that your furry companions are as comfortable as you are.</p>'
            as="div"
            className="da-body"
          />

          <div className="da-cards">
            <EditableSlotGroup groupPath="tabbedcallouts.amenity_1" definition={amenity1Def} className="da-card-item">
              <AmenityTab title={amenityTitles[0]} index={0} active={safeIndex === 0} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="tabbedcallouts.amenity_2" definition={amenity2Def} className="da-card-item">
              <AmenityTab title={amenityTitles[1]} index={1} active={safeIndex === 1} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="tabbedcallouts.amenity_3" definition={amenity3Def} className="da-card-item">
              <AmenityTab title={amenityTitles[2]} index={2} active={safeIndex === 2} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="tabbedcallouts.amenity_4" definition={amenity4Def} className="da-card-item">
              <AmenityTab title={amenityTitles[3]} index={3} active={safeIndex === 3} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="tabbedcallouts.amenity_5" definition={amenity5Def} className="da-card-item">
              <AmenityTab title={amenityTitles[4]} index={4} active={safeIndex === 4} onSelect={setActiveIndex} />
            </EditableSlotGroup>
            <EditableSlotGroup groupPath="tabbedcallouts.amenity_6" definition={amenity6Def} className="da-card-item">
              <AmenityTab title={amenityTitles[5]} index={5} active={safeIndex === 5} onSelect={setActiveIndex} />
            </EditableSlotGroup>
          </div>

          <EditableSlotGroup groupPath="tabbedcallouts.cta" definition={ctaDef} className="da-cta-wrap">
            {/* Solid fill in the PRIMARY brand color (--cta-accent), with the
                on-primary token for a legible label. On hover it flips to the
                same treatment as the Footer's Apply Now button — an on-primary
                (light) surface with a primary-colored label. */}
            <a
              href={cta.url || '/amenities'}
              target={cta._target || '_self'}
              className={`da-cta ${CTA_SOLID}`}
              style={ctaOverLight}
            >
              {cta.label || 'View All Amenities'}
            </a>
          </EditableSlotGroup>
        </div>

        {/* ── Right: arched image with a decorative primary-color circle behind ── */}
        <div className="da-media">
          <div className="da-media-circle" aria-hidden="true" />
          <div className="da-arch-frame">
            <div className="da-arch-clip">
              <EditableSlotGroup
                groupPath="tabbedcallouts.image"
                definition={imgDef}
                className="absolute inset-0"
              >
                {img.image ? (
                  <VibesitesImage
                    url={img.image}
                    alt={img.alt || ''}
                    sizes="(min-width: 1024px) 28rem, 100vw"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                )}
              </EditableSlotGroup>

              {/* Selected amenity photo — swaps in over the base image when the
                  active tab matches a relevant gallery photo. Every distinct
                  match is rendered once and kept mounted so it is decoded ahead
                  of time; selecting a tab just crossfades opacity. The active
                  layer sits above the others (higher z-index) and fades in while
                  the previous one stays fully opaque beneath it, so the swap
                  never flashes the base image or pops in an undecoded photo. */}
              {amenityImages.map((url, i) =>
                url ? (
                  <VibesitesImage
                    key={url}
                    url={url}
                    alt={safeIndex === i ? activeTitle || '' : ''}
                    aria-hidden={safeIndex !== i}
                    sizes="(min-width: 1024px) 28rem, 100vw"
                    className={`da-arch-overlay absolute inset-0 w-full h-full object-cover object-center${
                      safeIndex === i ? ' da-arch-overlay--active' : ''
                    }`}
                  />
                ) : null,
              )}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .discover-amenities {
          /* Background is the brand's secondary color so the section adopts the
             property's palette (was a fixed charcoal literal). The light text
             literals below assume a dark secondary for contrast. */
          background: var(--vibe-color-secondary);
          color: #FFFFFF;
        }

        .da-grid {
          max-width: 1240px;
          margin: 0 auto;
          padding: 3.5rem 1.5rem;
          display: grid;
          grid-template-columns: 1fr;
          gap: 2.5rem;
          align-items: center;
        }
        /* Two-column layout starts at tablet (768px) so tablet mirrors the
           desktop split — copy + amenity cards on the left, arched image on the
           right — instead of stacking. */
        @media (min-width: 768px) {
          .da-grid {
            grid-template-columns: 1.08fr 0.92fr;
          }
        }
        /* Tablet only (768–1023px): the arched image looks undersized because
           the narrow image column — not its max-width — is the real constraint.
           Give the image column more of the split and tighten the gap so the
           arch grows, and raise the frame's max-width so it can actually fill
           the roomier column. Desktop (≥1024px) keeps the original ratio. */
        @media (min-width: 768px) and (max-width: 1023px) {
          .da-grid {
            grid-template-columns: 0.92fr 1.08fr;
            gap: 2rem;
          }
          .da-arch-frame {
            max-width: 32rem;
          }
        }
        /* Desktop keeps the roomier gap + padding. */
        @media (min-width: 1024px) {
          .da-grid {
            gap: 4rem;
            padding: 5rem 3rem;
          }
        }

        /* On mobile (phones), surface the arched image between the intro copy
           and the amenity tabs so the photo (and the swap-on-select behavior)
           is visible before scrolling the tab list. display: contents flattens
           .da-text so its children participate in the column flex directly,
           letting the media slot between the body and the tabs via order. */
        @media (max-width: 767px) {
          .da-grid {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 0;
          }
          .da-text { display: contents; }
          .da-heading { order: 1; }
          .da-body { order: 2; }
          /* The decorative circle sits at top: -6% and pokes above the arched
             frame; add top spacing so it doesn't collide with the body copy in
             the stacked layout. */
          .da-media { order: 3; margin-top: 3rem; margin-bottom: 2.25rem; }
          .da-cards { order: 4; }
          .da-cta-wrap { order: 5; }
        }

        /* ── Left column ── */
        .da-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(2.25rem, 4vw, 3.25rem);
          font-weight: 700;
          line-height: 1.05;
          margin: 0 0 1.25rem;
          color: #FFFFFF;
        }

        .da-body {
          font-family: var(--vibe-font-body);
          font-size: 1.0625rem;
          line-height: 1.65;
          color: color-mix(in srgb, #FFFFFF 75%, transparent);
          margin: 0 0 1.75rem;
          max-width: 40rem;
        }

        /* Center the heading + body copy on mobile; revert to left-aligned once
           the section splits into two columns. */
        @media (max-width: 767px) {
          .da-heading { text-align: center; }
          .da-body {
            text-align: center;
            margin-left: auto;
            margin-right: auto;
          }
          /* Center the "View All Amenities" CTA on mobile to match the
             centered heading + body copy. */
          .da-cta-wrap { text-align: center; }
        }

        /* ── Amenities widget ── */
        .da-cards {
          list-style: none;
          margin: 0 0 2.25rem;
          padding: 0;
          display: grid;
          /* Fixed equal columns + equal row heights so every tab is identical
             in size regardless of label length. */
          grid-template-columns: repeat(3, minmax(0, 1fr));
          grid-auto-rows: 1fr;
          gap: 0.85rem;
          max-width: 44rem;
        }
        /* Keep two columns down to the smallest phones so the six amenities
           stay a short 3-row list (rather than a long single-column scroll),
           and tighten each card so both columns fit comfortably. */
        @media (max-width: 767px) {
          .da-cards {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 0.6rem;
          }
          /* Cards in a row share the tallest card's height (grid-auto-rows: 1fr).
             Vertically center each card's icon + label so a single-line tab
             (e.g. "Dog Park") sits centered rather than pinned to the top next
             to a taller multi-line neighbor. */
          .da-card {
            justify-content: center;
          }
        }
        .da-card-item {
          display: block;
          height: 100%;
        }
        /* Cards adopt the canonical CTA (.v-cta) visual language: an outline that
           fills through a ::before layer, an uppercase label, and a lift +
           soft shadow on interaction. Inactive outline/label use #262b22 for
           contrast on the light secondary section ground; hover/active fill
           with the brand primary and a white label. */
        .da-card {
          --cta-accent: #262b22;
          --cta-fill: var(--vibe-color-primary);
          --cta-on-accent: #FFFFFF;
          position: relative;
          z-index: 0;
          overflow: hidden;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          gap: 0.7rem;
          padding: 0.875rem 1.25rem;
          border: 1.5px solid var(--cta-accent);
          border-radius: var(--vibe-radius-sm, 2px);
          color: var(--cta-accent);
          font-family: var(--vibe-font-body);
          font-size: 0.8rem;
          font-weight: 400;
          text-transform: uppercase;
          letter-spacing: 0;
          text-align: left;
          background: transparent;
          cursor: pointer;
          appearance: none;
          transition: color 0.3s ease, background 0.3s ease, border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
        }
        /* Accent fill layer behind the label + icon — fades in on hover and for
           the active tab (mirrors .v-cta::before). */
        .da-card::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: -1;
          background: var(--cta-fill);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .da-card > * { position: relative; }
        .da-card:hover {
          color: var(--cta-on-accent);
          border-color: var(--cta-fill);
          transform: translateY(-2px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--cta-fill) 30%, transparent);
        }
        .da-card:hover::before { opacity: 1; }
        .da-card:active {
          transform: translateY(0);
          box-shadow: none;
        }
        .da-card:hover svg.da-card-icon {
          filter: none;
          color: var(--cta-on-accent);
        }
        .da-card:focus-visible {
          outline: 2px solid var(--vibe-color-primary, #B28C59);
          outline-offset: 2px;
        }
        /* Active/selected tab reads as the filled CTA: accent fill, on-accent label. */
        .da-card--active {
          color: var(--cta-on-accent);
          border-color: var(--cta-fill);
        }
        .da-card--active::before { opacity: 1; }
        .da-card--active svg.da-card-icon {
          filter: none;
          color: var(--cta-on-accent);
        }
        .da-card-label {
          line-height: 1.2;
          /* min-width: 0 lets the label wrap below its intrinsic width inside
             the flex row instead of forcing the card wider. */
          min-width: 0;
          /* Prefer whitespace wrapping so multi-word titles flow onto new lines
             with each word intact. word-break: normal keeps words from splitting
             mid-word ("COMMUNIT Y"), while overflow-wrap: break-word is a last
             resort: a single word wider than the card (e.g. "LANDSCAPING" in a
             narrow desktop column) breaks instead of bleeding past the border.
             break-word only triggers when a word can't otherwise fit, so titles
             that do fit are never split. text-wrap: balance evens the lines. */
          overflow-wrap: break-word;
          word-break: normal;
          hyphens: manual;
          text-wrap: balance;
        }
        .da-card-icon {
          width: 26px;
          height: 26px;
          object-fit: contain;
          flex-shrink: 0;
          color: var(--cta-accent);
        }

        /* Stacked card layout. Declared AFTER the base .da-card /
           .da-card-icon rules on purpose: media queries add no specificity, so
           these overrides only win by coming later in source order. The icon +
           label row layout (the base rule) only has room for long single words
           like "LANDSCAPING" once the card is wide, which needs a window of
           ~1280px+. Below that — through the whole tablet range AND the cramped
           1024–1279px band where the section is already two columns but the
           card column is narrow — stack the icon above a full-width,
           center-aligned, slightly smaller label so long words keep the card's
           full width and stay whole instead of breaking mid-word. */
        @media (max-width: 1279px) {
          .da-card {
            flex-direction: column;
            /* Center content on both axes: align-items handles the horizontal
               (cross) axis and justify-content the vertical (main) axis, so a
               short card sitting at the shared row height (grid-auto-rows: 1fr)
               keeps its icon + label vertically centered instead of pinned to
               the top next to a taller multi-line neighbor. */
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 0.85rem 0.6rem;
            gap: 0.5rem;
            font-size: 0.72rem;
            letter-spacing: 0;
          }
          .da-card-icon {
            width: 22px;
            height: 22px;
          }
        }

        /* ── CTA (canonical .v-cta styling from _cta.tsx) ── */
        .da-cta-wrap {
          display: block;
        }
        .da-cta {
          letter-spacing: 0;
        }
        /* Hover matches the Footer's Apply Now button (.resident-login-cta):
           flip from the primary fill to an on-primary (light) surface with a
           primary-colored label + border, and suppress the ::before darkening
           overlay so the flat fill shows cleanly. Higher specificity than the
           shared .v-cta--solid:hover so it wins. */
        .da-cta.v-cta--solid:hover {
          color: var(--vibe-color-primary);
          background: var(--vibe-color-text-on-primary);
          border-color: var(--vibe-color-text-on-primary);
        }
        .da-cta.v-cta--solid:hover::before {
          opacity: 0;
        }

        /* ── Right column: arched image frame (matches Property Overview) ── */
        .da-media {
          position: relative;
          width: 100%;
        }
        /* Decorative circle sitting behind the arched image, offset toward the
           top-right so it peeks out from behind the photo. Uses the brand
           primary color. */
        .da-media-circle {
          position: absolute;
          z-index: 0;
          top: -6%;
          left: 50%;
          transform: translateX(calc(-50% + 22%));
          width: 21rem;
          max-width: 78%;
          aspect-ratio: 1 / 1;
          border-radius: 9999px;
          background: var(--vibe-color-primary);
          pointer-events: none;
        }
        .da-arch-frame {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 28rem;
          margin: 0 auto;
          border-radius: 50% 50% 6px 6px / 42% 42% 6px 6px;
          padding: 14px;
        }
        .da-arch-clip {
          position: relative;
          aspect-ratio: 8 / 9;
          border-radius: 50% 50% 3px 3px / 40% 40% 3px 3px;
          overflow: hidden;
        }
        /* All matched amenity photos stay mounted and stacked; only opacity
           changes on select. The inactive layers rest beneath (z-index 1,
           opacity 0); the active layer rises on top (z-index 2) and fades in
           while the previously-active layer remains opaque underneath until the
           crossfade completes — no base-image bleed-through, no undecoded pop. */
        .da-arch-overlay {
          z-index: 1;
          opacity: 0;
          transition: opacity 0.35s ease;
        }
        .da-arch-overlay--active {
          z-index: 2;
          opacity: 1;
        }
        @media (prefers-reduced-motion: reduce) {
          .da-arch-overlay { transition: none; }
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'TabbedCallouts',
  displayName: 'Discover Amenities',
  tokens: {
    'tabbedcallouts.section_heading': {
      label: 'Section Heading',
      type: 'richtext',
      defaultValue: '<p>Discover Amenities</p>',
    },
    'tabbedcallouts.body': {
      label: 'Body Text',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      defaultValue:
        '<p>At Verso, we offer a range of luxurious amenities designed to elevate your living experience. Enjoy resort-style features such as high-end fixtures, a resort-style pool, and beautifully landscaped outdoor spaces perfect for relaxation or socializing. Our pet-friendly apartment community also includes a dedicated dog park and pet-washing station ensuring that your furry companions are as comfortable as you are.</p>',
    },
  },
  slotGroups: {
    'tabbedcallouts.image': imageDef,
    'tabbedcallouts.cta': ctaDef,
    'tabbedcallouts.amenity_1': amenity1Def,
    'tabbedcallouts.amenity_2': amenity2Def,
    'tabbedcallouts.amenity_3': amenity3Def,
    'tabbedcallouts.amenity_4': amenity4Def,
    'tabbedcallouts.amenity_5': amenity5Def,
    'tabbedcallouts.amenity_6': amenity6Def,
  },
};

(TabbedCallouts as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default TabbedCallouts;
